import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import * as fflate from "fflate";
import { base64url } from "rfc4648";
import Ajv, { Schema } from "ajv";
import type { GenMessage } from "@bufbuild/protobuf/codegenv1";
import { Message, toBinary, fromBinary, fromJson } from "@bufbuild/protobuf";

interface decodedResult<TSelected> {
    hasError: boolean;
    errorMessage: string | undefined;
    decodedData: TSelected | undefined;
}
interface encodedResult {
    hasError: boolean;
    errorMessage: string | undefined;
    base64UrlCompressed: string | undefined;
}

export const usePermalinks = <TSelected>(
    ajvSchema: Schema,
    protobufSchema: GenMessage<Message & { value: unknown }>,
) => {
    const validateSchema = useMemo(() => {
        const ajv = new Ajv({
            useDefaults: true,
            strict: false,
        });
        return ajv.compile(ajvSchema);
    }, [ajvSchema]);

    const hashedvalue = "hashedvalue";
    const params = useParams();

    const decodePermalink = useCallback((): decodedResult<TSelected> => {
        const result = {} as decodedResult<TSelected>;

        // When we have a hashValue
        if (params[hashedvalue]) {
            try {
                const parsedParamValue = base64url.parse(params[hashedvalue]);

                const deCompressedParamValue =
                    fflate.decompressSync(parsedParamValue);

                const deCompressedProtoObject = fromBinary(
                    protobufSchema,
                    deCompressedParamValue,
                );

                if (validateSchema) {
                    // By stringify and JSON.Parse we make sure the object string values are validated correctly
                    const jsonProto = JSON.stringify(
                        deCompressedProtoObject.value,
                    );
                    const jsonObject = JSON.parse(jsonProto);

                    const isJSONValid = validateSchema(jsonObject);
                    if (isJSONValid) {
                        result.decodedData = jsonObject as TSelected;
                    } else {
                        throw new Error("Invalid json");
                    }
                } else {
                    throw new Error("Invalid ajv schema.");
                }
            } catch (error) {
                result.hasError = true;
                result.errorMessage =
                    error instanceof Error ? error.message : "Unknown error";
            }
        }
        return result;
    }, [params, protobufSchema, validateSchema]);

    const encodePermalink = useCallback(
        (stateValue: TSelected | undefined): encodedResult => {
            const result = {} as encodedResult;

            if (stateValue) {
                try {
                    const jsonString = JSON.stringify(stateValue);

                    // Convert JSON to proto container
                    const protoContainer = {
                        value: JSON.parse(jsonString),
                    };

                    const protoObjFromJson = fromJson(
                        protobufSchema,
                        protoContainer,
                        {
                            ignoreUnknownFields: true,
                        },
                    );

                    // shrink it
                    const binary = toBinary(protobufSchema, protoObjFromJson);
                    const compressed = fflate.compressSync(binary, {
                        mtime: 0,
                    });
                    result.base64UrlCompressed =
                        base64url.stringify(compressed);
                } catch (error) {
                    result.hasError = true;
                    result.errorMessage =
                        error instanceof Error
                            ? error.message
                            : "Unknown error";

                    console.error("Something went wrong", result.errorMessage);
                }
            }
            return result;
        },
        [protobufSchema],
    );

    return { decodePermalink, encodePermalink };
};
